import { update, prop } from 'ramda'
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { UnstyledButton } from '@/components/anchor'
import { SectionHeader } from '@/components/headings'
import ListBox from '@/components/list-box'
import Section from '@/components/section'
import { Strong } from '@/components/typography'
import useRole from '@/hooks/use-role'
import { colors, fontSizes, fontWeights } from '@/styles'
import formatAddendumPreview from '@/utils/format-addendum-preview'
import InfoIcon from './info-icon.svg'
import { showAddendumModalWizard } from './addendum-modal-wizard'
import { showAddendumModalView } from './addendum-modal-view'
import formatVariables from '../format-variables'

const Row = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  flex-direction: row;
  padding: 24px;
  border: 1px solid ${colors.grey88};
  border-radius: 8px;
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06);

  &:hover {
    background-color: ${colors.lightGrey};
  }
`

const Left = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 44px;
  flex: 9999;
  margin-right: 34px;
  overflow: hidden;
`

const InfoIconWrapper = styled.div`
  flex: 0.1;
  flex-basis: 20px;
  height: 20px;
  background-image: url(${InfoIcon});
  background-position: center right;
  background-repeat: no-repeat;
`

const SignButton = styled.p`
  flex: 0.1;
  flex-basis: 26px;
  color: ${colors.link};
  font-size: 14px;
  margin: 0;
`

const CustomTitle = styled(Strong)`
  font-weight: ${fontWeights.medium};
  font-size: ${({ fontSize }) => fontSize};
`

const CustomDescription = styled.span`
  font-size: ${fontSizes.small};
  color: ${colors.grey56};
  overflow: hidden;
  text-overflow: ellipsis;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`

const hasSigned = (signatures, type) => userId =>
  signatures.filter(signature => signature.userId === userId && signature.userType === type)
    .length > 0

const Addendums = ({ disabled, contract, signatures }) => {
  const { t } = useTranslation()
  const hasUserSigned = hasSigned(signatures, 'user')
  const session = useSelector(prop('session')) || {}
  const userId = session.session?.id
  const role = useRole()
  const isTenant = role === 'tenant'
  const initialAddendums = [...contract.custom_addendums].map(addendum => ({
    ...addendum,
    complete: hasUserSigned(userId, signatures) || !isTenant || contract.is_complete
  }))

  const [addendums, setAddendums] = useState(initialAddendums)
  const dispatch = useDispatch()
  const completeStep = useCallback(
    () => dispatch.contract.completeStep('review'),
    [dispatch.contract]
  )
  const incompleteStep = useCallback(
    () => dispatch.contract.incompleteStep('review'),
    [dispatch.contract]
  )
  const formattedVariables = formatVariables(contract)
  const contractComplete = contract.is_complete

  const onAgreementChange = item => {
    const itemIndex = addendums.findIndex(addendum => item.id === addendum.id)
    const updatedAddendum = {
      ...item,
      complete: !item.complete
    }

    setAddendums(current => update(itemIndex, updatedAddendum, current))
  }

  useEffect(() => {
    if (addendums.length && addendums.some(({ complete }) => !complete)) {
      incompleteStep()
    } else {
      completeStep()
    }
  }, [addendums, completeStep, incompleteStep])

  const getOnChange = () => {
    return isTenant && !disabled ? onAgreementChange : null
  }

  const openAddendumWizard = key => {
    showAddendumModalWizard({
      data: addendums,
      activeStep: key,
      onChange: getOnChange(),
      contractId: contract.id,
      variables: formattedVariables
    })
  }

  const toggleAddendumView = (addendum, signed) => {
    showAddendumModalView({
      addendum,
      contractId: contract.id,
      variables: formattedVariables,
      signed
    })
  }

  const clickAddendum = item => {
    if (contractComplete || (isTenant && item.complete)) {
      toggleAddendumView(item, true)
    } else if (!isTenant || disabled) {
      toggleAddendumView(item)
    } else {
      openAddendumWizard(item.id)
    }
  }

  const renderAddendumRow = item => {
    const fontSize = fontSizes.regular

    return (
      <Row key={item.id} onClick={() => clickAddendum(item)}>
        <Left>
          <CustomTitle fontSize={fontSize}>{item.title}</CustomTitle>
          <CustomDescription>
            {formatAddendumPreview(item, t, formattedVariables)}
          </CustomDescription>
        </Left>
        {isTenant && !disabled && !item.complete ? (
          <SignButton>{t('r.contract.view.addendums_sign.button', 'Sign')}</SignButton>
        ) : (
          <InfoIconWrapper />
        )}
      </Row>
    )
  }

  if (addendums.length === 0) {
    return null
  }

  return (
    <Section>
      <SectionHeader>
        {t('b.contract.view.addendums.text', 'Addendums')} ({addendums.length})
      </SectionHeader>
      <p>
        {t(
          'b.contract.view.addendums_description.text',
          'The following Addendums are attached as schedules to this Agreement and incorporated by reference into this Agreement.'
        )}
      </p>
      <ListBox data={addendums} renderItem={item => renderAddendumRow(item)} />
    </Section>
  )
}

Addendums.defaultProps = {
  contract: {},
  userType: null,
  disabled: false
}

export default Addendums
